import { useState } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Flex, Table, Tooltip, Typography, message } from 'antd';
import { useSearchParams } from 'react-router-dom';

import { useGetCreatorContent } from '@/api/creators';
import { useDebounce } from '@/hooks';
import { prepareTableColumnsWithQueryParams } from '@/lib/helpers/prepareTableColumnsWithQueryParams';
import client from '@/lib/http/client';
import { IPagination, IResponseError, TCreatorsSortBy, TSortOrder } from '@/ts';

import { columns } from '../Creator/data';

export default function CreatorsContent({ id }: { id: string }) {
	const [searchParams, setSearchParams] = useSearchParams({});
	const [search, setSearch] = useState('');
	const [current, setCurrent] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isAllDownloading, setIsAllDownloading] = useState(false);

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(searchParams.get('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(searchParams.get('limit') ?? 10),
	});
	const [sortBy, setSortBy] = useState<TCreatorsSortBy>(
		(searchParams.get('sortBy') as TCreatorsSortBy) ?? 'posted',
	);
	const [sortOrder, setSortOrder] = useState<TSortOrder>(
		(searchParams.get('sortOrder') as TSortOrder) ?? 'asc',
	);

	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isFetching } = useGetCreatorContent(id, {
		pagination,
		sortBy,
		sortOrder,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const contentItems = data?.data?.data?.items;
	const total = data?.data?.data?.pagination?.total ?? 0;

	const onTableChange = (__: any, filters: any, sorter: any) => {
		const { field = 'posted', order = 'ascend' } = sorter;
		const platform = filters.platform;

		if (field) {
			setSortBy(field);
			setSearchParams((searchParams) => {
				searchParams.set('sortBy', field);
				return searchParams;
			});
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			setSearchParams((searchParams) => {
				searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
				return searchParams;
			});
		}

		if (platform) {
			//   setFilters(platform as string[]);
			setSearchParams((searchParams) => {
				searchParams.set('filters', platform.join(','));
				return searchParams;
			});
		} else {
			setSearchParams((searchParams) => {
				searchParams.set('filters', '');
				return searchParams;
			});
			//   setFilters([]);
		}
	};

	const handleDownload = async (_id: string) => {
		try {
			setCurrent(_id);
			setIsDownloading(true);
			const response = await client.get<any>(
				`/admin/content/json/single/${_id}`,
				{
					responseType: 'blob',
				},
			);

			const url = window.URL.createObjectURL(response.data);

			// Create a link element and trigger download
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `data_${_id}.zip`);
			document.body.appendChild(link);
			link.click();

			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Download failed:', error);
			const err = error as IResponseError;
			message.error(
				`Download failed: ${err?.message || err?.response?.data?.message}`,
			);
		} finally {
			setIsDownloading(false);
			setCurrent('');
		}
	};

	const handleDownloadAllContent = async () => {
		try {
			setIsAllDownloading(true);
			const response = await client.get<any>(
				`/admin/content/json/creator/${id}`,
				{
					responseType: 'blob',
				},
			);

			const url = window.URL.createObjectURL(response.data);

			// Create a link element and trigger download
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `data_creator_${id}.zip`);
			document.body.appendChild(link);
			link.click();

			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Download failed:', error);
			const err = error as IResponseError;
			message.error(
				`Download failed: ${err?.message || err?.response?.data?.message}`,
			);
		} finally {
			setIsAllDownloading(false);
			setCurrent('');
		}
	};

	return (
		<Flex vertical gap={16} style={{ width: '100%' }}>
			<Flex justify="space-between">
				<Typography.Title level={5} style={{ margin: 0 }}>
					Creators content
				</Typography.Title>

				{/* {total > 0 && (
					<Button
						type="default"
						disabled={isAllDownloading}
						loading={isAllDownloading}
						onClick={() => handleDownloadAllContent()}
					>
						<DownloadOutlined />
						Download all content
					</Button>
				)} */}
			</Flex>

			<Table
				loading={
					isLoading || isFetching
					//   {
					// 	spinning: isLoading || isFetching,
					// 	indicator: <Loader />,
					// }
				}
				style={{ width: '100%' }}
				size="small"
				scroll={{ x: 700 }}
				onChange={onTableChange}
				dataSource={contentItems ?? []}
				columns={[
					...prepareTableColumnsWithQueryParams(columns, searchParams),
					// {
					// 	key: 'actions',
					// 	title: 'Actions',
					// 	width: 40,
					// 	align: 'center',
					// 	render: (_, record) => (
					// 		<Tooltip title="Download JSON data" placement="left">
					// 			<Button
					// 				size="small"
					// 				loading={current === record._id && isDownloading}
					// 				disabled={isDownloading}
					// 				onClick={() => handleDownload(record._id)}
					// 			>
					// 				<DownloadOutlined />
					// 			</Button>
					// 		</Tooltip>
					// 	),
					// },
				]}
				rowKey={'_id'}
				bordered
				// rowSelection={rowSelection}
				pagination={{
					total: total,
					current: pagination.page,
					showSizeChanger: true,
					pageSizeOptions: [10, 25, 50],
					onChange: (page: number, pageSize: number) => {
						setPagination((cur) => ({ ...cur, page, limit: pageSize }));
						setSearchParams((searchParams) => {
							searchParams.set('page', page.toString());
							searchParams.set('limit', pageSize.toString());
							return searchParams;
						});
					},
				}}
			/>
		</Flex>
	);
}
